import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import { FaChevronDown, FaArrowRight } from 'react-icons/fa';
import './FAQ.css';

const FAQPage = () => {
  const [openIndex, setOpenIndex] = useState(null);

  const faqs = [
    {
      question: "What destinations do you cover?",
      answer: "We focus primarily on Southeast Asia (Thailand, Vietnam, Cambodia, Indonesia, Malaysia, Singapore, Laos, Philippines) and South Asia (Sri Lanka), with additional coverage of Middle Eastern destinations like Dubai. Our guides are based on personal travel experiences."
    },
    {
      question: "How do I plan a trip with you?",
      answer: "Use our Plan Your Trip page to share your preferences, budget, and travel dates. We'll provide personalized destination recommendations, sample itineraries, and practical tips. You can also explore our destination guides and blog posts for inspiration."
    },
    {
      question: "Do you offer budget travel options?",
      answer: "Yes! Our guides cover a range of budgets from backpacker-friendly options to luxury experiences. We include affordable accommodation, street food recommendations, and free or low-cost activities alongside premium options."
    },
    {
      question: "What's the best time to visit Southeast Asia?",
      answer: "Generally, November to February is ideal for most Southeast Asian countries, offering cooler temperatures and less rain. However, each destination has its own best season—check our individual country guides for specific recommendations."
    },
    {
      question: "Do you provide visa assistance?",
      answer: "While we don't process visas, our destination guides include detailed visa requirements, application procedures, and tips for each country. We'll point you to official resources and share our experiences with visa applications."
    },
    {
      question: "Are your travel guides based on personal experience?",
      answer: "Yes! Every destination we write about is based on our personal travels. We share real experiences, honest recommendations, and practical tips we wish we'd known before visiting. Authenticity is our priority."
    },
    {
      question: "Can you help with solo travel?",
      answer: "Absolutely! We've done extensive solo and couple travel across Asia. Our guides include safety tips, solo-friendly accommodations, ways to meet other travelers, and destinations particularly welcoming to solo adventurers."
    },
    {
      question: "Do you recommend hotels and tours?",
      answer: "Yes, our destination guides include accommodation recommendations across different budget ranges and suggest both organized tours and DIY options. We focus on authentic experiences and value-for-money choices based on our personal stays and research."
    },
    {
      question: "How accurate is your travel information?",
      answer: "We update our guides regularly and verify information before publishing. However, travel details can change, so we always recommend double-checking visa requirements, opening hours, and prices closer to your travel date."
    },
    {
      question: "Do you offer group tours or travel packages?",
      answer: "Currently, we focus on providing comprehensive guides and itineraries for independent travelers. However, we do recommend trusted tour operators and can help connect you with local guides in various destinations."
    },
    {
      question: "Can I contribute my travel experiences?",
      answer: "We love hearing from fellow travelers! While we write all content ourselves to maintain consistency, you can share your experiences and tips through comments on our blog posts or by contacting us directly."
    },
    {
      question: "How do you make money from this blog?",
      answer: "We may earn commissions from affiliate links (clearly marked) when you book through our recommendations. We only recommend services we've personally used or thoroughly researched. Your support helps us keep creating free content!"
    }
  ];

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <>
      <Helmet>
        <title>Frequently Asked Questions - Safarnamah Travel Guide</title>
        <meta name="description" content="Everything you need to know about planning your Asian adventure with Safarnamah. Get answers to common travel questions about destinations, budgets, visas, and more." />
        <meta name="keywords" content="travel FAQ, Asia travel questions, trip planning help, travel guide questions, Southeast Asia FAQ" />
        
        {/* FAQPage Schema */}
        <script type="application/ld+json">
          {JSON.stringify(faqSchema)}
        </script>
      </Helmet>

      <div className="faq-page">
        {/* Hero Section */}
        <motion.section 
          className="faq-hero"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6 }}
        >
          <div className="container">
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              Frequently Asked Questions
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              Everything you need to know about planning your Asian adventure
            </motion.p>
          </div>
        </motion.section>

        {/* FAQ List Section */}
        <section className="faq-content">
          <div className="container">
            <div className="faq-grid">
              {faqs.map((faq, index) => (
                <motion.div
                  key={index}
                  className="faq-card"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.05 }}
                >
                  <button
                    className={`faq-question ${openIndex === index ? 'active' : ''}`}
                    onClick={() => toggleFAQ(index)}
                    aria-expanded={openIndex === index}
                  >
                    <span className="faq-number">{(index + 1).toString().padStart(2, '0')}</span>
                    <span className="faq-question-text">{faq.question}</span>
                    <FaChevronDown className={`faq-chevron ${openIndex === index ? 'rotate' : ''}`} />
                  </button>
                  <div className={`faq-answer ${openIndex === index ? 'show' : ''}`}>
                    <p>{faq.answer}</p>
                  </div>
                </motion.div>
              ))}
            </div>

            {/* CTA Section */}
            <motion.div
              className="faq-cta"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <div className="faq-cta-content">
                <h2>Still have questions?</h2>
                <p>We're here to help! Reach out to us and we'll get back to you as soon as possible.</p>
              </div>
              <div className="faq-cta-buttons">
                <a href="/contact" className="btn btn-primary">
                  Contact Us
                  <FaArrowRight className="btn-icon" />
                </a>
                <a href="/plan-trip" className="btn btn-secondary">
                  Plan Your Trip
                  <FaArrowRight className="btn-icon" />
                </a>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default FAQPage;
